import { useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useFetch } from "../../lib/useData";
import { PageTitle, Badge, TableWrap, Btn, Modal, Input, Textarea, Select } from "../../components/admin/ui";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const PAGE_SIZES = [10, 50, 100, 500, 1000];

export default function SevaAdmin() {
  const { data: sevas, loading, reload } = useFetch("/admin/sevas");
  const { data: cats } = useFetch("/admin/seva_categories");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(10);
const [page, setPage] = useState(1);

const allRows = sevas || [];
const total = allRows.length;
const totalPages = Math.max(1, Math.ceil(total / pageSize));
const currentPage = Math.min(page, totalPages);
const start = (currentPage - 1) * pageSize;
const pagedRows = allRows.slice(start, start + pageSize);

  const blank = { name: "", slug: "", category_id: "", category_name: "", short_description: "", description: "",
    price: 0, duration: "", image: "", featured: false, available_days: [...DAYS],
    time_slots: [{ time: "7:30 AM", capacity: 10, disabled: false }], blocked_dates: [],
    instructions: "", cancellation_rules: "", display_order: 1, status: "draft" };

  const openNew = () => { setForm(blank); setEditing("new"); };
  const openEdit = (row) => { setForm({ ...blank, ...row }); setEditing(row.id); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDay = (d) => set("available_days", form.available_days?.includes(d) ? form.available_days.filter((x) => x !== d) : [...(form.available_days || []), d]);
  const setSlot = (i, k, v) => set("time_slots", form.time_slots.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const addSlot = () => set("time_slots", [...(form.time_slots || []), { time: "12:00 PM", capacity: 10, disabled: false }]);
  const rmSlot = (i) => set("time_slots", form.time_slots.filter((_, idx) => idx !== i));

  // const save = async () => {
  //   setSaving(true);
  const save = async () => {
  if (!form.slug?.trim()) {
    toast.error("Slug is required");
    return;
  }

  setSaving(true);
    try {
      const cat = (cats || []).find((c) => c.id === form.category_id);
      const payload = { ...form, category_name: cat?.name || form.category_name };
      if (editing === "new") await api.post("/admin/sevas", payload);
      else await api.put(`/admin/sevas/${editing}`, payload);
      toast.success("Saved"); setEditing(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try { await api.delete(`/admin/sevas/${row.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Seva & Pooja" desc="Manage seva catalogue, prices, slots & capacity">
        <Btn onClick={openNew} testid="seva-add"><Plus className="w-4 h-4" /> Add Seva</Btn>
      </PageTitle>
      <TableWrap>
        <thead className="bg-slate-800 text-slate-400"><tr>
          <th className="text-left px-4 py-3">Image</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Price</th><th className="text-left px-4 py-3">Slots</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-700" data-testid="seva-admin-table">
          {loading ? <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr> :
            pagedRows.map((s) => (
              <tr key={s.id} className="hover:bg-slate-800/50">
                <td className="px-4 py-3"><img src={s.image} alt="" className="w-12 h-12 rounded-lg object-cover" /></td>
                <td className="px-4 py-3 font-semibold">{s.name}</td>
                <td className="px-4 py-3 text-slate-300">{s.category_name}</td>
                <td className="px-4 py-3 text-orange-400 font-bold">₹{s.price}</td>
                <td className="px-4 py-3 text-slate-400">{s.time_slots?.length || 0}</td>
                <td className="px-4 py-3"><Badge status={s.status} /></td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  <button onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-slate-700 text-blue-400" data-testid={`seva-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(s)} className="p-2 rounded-lg hover:bg-slate-700 text-red-400" data-testid={`seva-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
        </tbody>
      </TableWrap>
      {!loading && total > 0 && (
  <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-slate-300">
    <div className="flex items-center gap-2">
      <span>Show</span>
      <select
        value={pageSize}
        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
        className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-200"
        data-testid="seva-page-size"
      >
        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <span>per page</span>
    </div>

    <span className="text-slate-400">
      {start + 1}–{Math.min(start + pageSize, total)} of {total}
    </span>

    <div className="flex items-center gap-2">
      <button
        onClick={() => setPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Prev</button>
      <span>{currentPage} / {totalPages}</span>
      <button
        onClick={() => setPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Next</button>
    </div>
  </div>
)}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Add Seva" : "Edit Seva"} wide>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Name *" value={form.name} onChange={(v) => set("name", v)} testid="seva-f-name" />
          <Input label="Slug" value={form.slug} onChange={(v) => set("slug", v)} testid="seva-f-slug" required />
          <Select label="Category *" value={form.category_id} onChange={(v) => set("category_id", v)} options={[{ value: "", label: "Select…" }, ...(cats || []).map((c) => ({ value: c.id, label: c.name }))]} testid="seva-f-cat" />
          <Input label="Price (₹)" type="number" value={form.price} onChange={(v) => set("price", Number(v))} testid="seva-f-price" />
          <Input label="Duration" value={form.duration} onChange={(v) => set("duration", v)} testid="seva-f-duration" />
          <Input label="Display Order" type="number" value={form.display_order} onChange={(v) => set("display_order", Number(v))} />
          <div className="sm:col-span-2"><Input label="Image URL" value={form.image} onChange={(v) => set("image", v)} testid="seva-f-image" /></div>
          <div className="sm:col-span-2"><Textarea label="Short Description" value={form.short_description} onChange={(v) => set("short_description", v)} /></div>
          <div className="sm:col-span-2"><Textarea label="Detailed Description" value={form.description} onChange={(v) => set("description", v)} rows={4} /></div>
          <div className="sm:col-span-2"><Textarea label="Instructions" value={form.instructions} onChange={(v) => set("instructions", v)} rows={2} /></div>
          <div className="sm:col-span-2"><Textarea label="Cancellation Rules" value={form.cancellation_rules} onChange={(v) => set("cancellation_rules", v)} rows={2} /></div>

          <div className="sm:col-span-2">
            <label className="block text-sm font-semibold text-slate-300 mb-2">Available Days</label>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button key={d} onClick={() => toggleDay(d)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${form.available_days?.includes(d) ? "bg-orange-600 text-white" : "bg-slate-700 text-slate-300"}`} data-testid={`seva-day-${d}`}>{d.slice(0, 3)}</button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-slate-300">Time Slots & Capacity</label>
              <Btn variant="outline" onClick={addSlot} testid="seva-add-slot"><Plus className="w-3 h-3" /> Slot</Btn>
            </div>
            <div className="space-y-2">
              {(form.time_slots || []).map((sl, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-900 rounded-lg p-2">
                  <input value={sl.time} onChange={(e) => setSlot(i, "time", e.target.value)} placeholder="Time" className="flex-1 rounded bg-slate-800 border border-slate-600 px-2 py-1.5 text-sm" data-testid={`slot-time-${i}`} />
                  <input type="number" value={sl.capacity} onChange={(e) => setSlot(i, "capacity", Number(e.target.value))} placeholder="Cap" className="w-20 rounded bg-slate-800 border border-slate-600 px-2 py-1.5 text-sm" data-testid={`slot-cap-${i}`} />
                  <label className="flex items-center gap-1 text-xs text-slate-400"><input type="checkbox" checked={sl.disabled} onChange={(e) => setSlot(i, "disabled", e.target.checked)} /> Off</label>
                  <button onClick={() => rmSlot(i)} className="text-red-400 p-1"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <Select label="Featured" value={String(form.featured)} onChange={(v) => set("featured", v === "true")} options={[{ value: "false", label: "No" }, { value: "true", label: "Yes" }]} />
          <Select label="Status" value={form.status} onChange={(v) => set("status", v)} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} testid="seva-f-status" />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} testid="seva-save">{saving ? "Saving…" : "Save Seva"}</Btn>
        </div>
      </Modal>
    </div>
  );
}
